import React, { useEffect, useMemo, useState } from 'react';
import TableHeader from './parts/TableHeader';
import TableBody from './parts/TableBody';
import HeaderBar from './parts/HeaderBar';
import {
  AllowedOptionColumn,
  AllowedOptionRow,
  AllowedOptionsSelectorTableProps,
} from './types';

type Props = AllowedOptionsSelectorTableProps & {
  columns: AllowedOptionColumn[];
  onChangeSingleSelection?: (changedRow: AllowedOptionRow) => void;
  onChangeAllSelection?: (updatedRows: AllowedOptionRow[]) => void;
};

const AllowedOptionsSelectorTable: React.FC<Props> = ({
  title,
  rows,
  columns,
  onChangeSingleSelection,
  onChangeAllSelection,
}) => {
  // 🔹 Estado local para comportamiento optimista
  const [localRows, setLocalRows] = useState<AllowedOptionRow[]>(rows ?? []);

  // 🔹 Sincroniza si el padre trae datos nuevos (e.g. después de guardar/recargar)
  useEffect(() => {
    setLocalRows(rows ?? []);
  }, [rows]);

  const { selectedCount, allCount } = useMemo(() => {
    const selected = localRows.filter((r) => r.selected);
    return {
      selectedCount: selected.length,
      allCount: localRows.length,
    };
  }, [localRows]);

  const toggleAll = (checked: boolean) => {
    // Optimista: actualiza UI al instante
    const updated = localRows.map((r) => ({ ...r, selected: checked }));
    setLocalRows(updated);
    // Notifica al padre para persistir (batch)
    onChangeAllSelection?.(updated);
  };

  const toggleOne = (idx: number) => {
    // Optimista: actualiza UI al instante
    const updated = [...localRows];
    updated[idx] = { ...updated[idx], selected: !updated[idx].selected };
    setLocalRows(updated);
    // Notifica al padre para persistir (single)
    onChangeSingleSelection?.(updated[idx]);
  };

  const allChecked = allCount > 0 && selectedCount === allCount;

  return (
    <div className="mt-6 w-full overflow-hidden rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
      <HeaderBar
        title={title}
        selectedCount={selectedCount}
        allSelectableCount={allCount}
      />

      <div className="overflow-x-auto">
        <table className="w-full table-auto border-collapse">
          <TableHeader
            allChecked={allChecked}
            onToggleAll={toggleAll}
            columns={columns}
          />
          <TableBody
            rows={localRows}
            onToggleOne={toggleOne}
            columns={columns}
          />
        </table>
      </div>
    </div>
  );
};

export default AllowedOptionsSelectorTable;
